import { useState } from 'react';
import { getImageUrl } from '../../../config';
import initials from '../../../lib/initials';
// .pf-avatar* lives in profile.css; imported here so Avatar is styled wherever
// it is used (department roster, home), not only once ProfilePage has loaded.
import '../profile.css';

/**
 * A photo-or-initials avatar.
 *
 * Every avatar on the profile goes through here so that ALL of them recover
 * from a broken image URL -- before this existed, only the reporting line did,
 * and a stale upload path left an empty box on the hero and the tool tiles.
 *
 * The photo carries alt="" because the person's name is always rendered as
 * text beside it; giving the image the name too makes a screen reader say it
 * twice.
 */

// Light tints with dark initials, drawn from the DS ink ramp. Solid tiles made
// a grid of 170 people a checkerboard louder than their names, and a red tile
// read as an error or a button (red is reserved for actions). Each pair is
// >= 6.5:1.
const TILE_COLORS = [
  { bg: '#E7ECEB', fg: '#3B4B48' }, // slate
  { bg: '#F0EAD6', fg: '#5E4F17' }, // bone
  { bg: '#E6E4D4', fg: '#2B2B29' }, // ink
  { bg: '#EFE3D6', fg: '#5E4329' }, // umber
  { bg: '#E4E9DF', fg: '#3F4B30' }, // sage
];

/**
 * A stable colour per person, hashed from the name, so a colleague keeps the
 * same tile between renders and across everyone's screens without storing
 * anything.
 */
function tileColor(name) {
  let h = 0;
  for (let i = 0; i < (name || '').length; i += 1) h = (h * 31 + name.charCodeAt(i)) % 997;
  return TILE_COLORS[h % TILE_COLORS.length];
}

export default function Avatar({ name, src, size = 'node', variant = 'brand', className = '' }) {
  // Remember WHICH source failed, not just that one did: a card keeps its key
  // across refetches, so a person whose photo later changes must get the new
  // image instead of staying on initials forever.
  const [brokenSrc, setBrokenSrc] = useState(null);
  const resolved = src && src !== brokenSrc ? getImageUrl(src) : '';
  const base = `pf-avatar pf-avatar--${size} ${className}`.trim();

  if (resolved) {
    return (
      <img
        className={base}
        src={resolved}
        alt=""
        onError={() => setBrokenSrc(src)}
      />
    );
  }

  return (
    <span
      className={`${base} pf-avatar--fallback`}
      style={variant === 'hashed' ? { background: tileColor(name).bg, color: tileColor(name).fg } : undefined}
      aria-hidden="true"
    >
      {initials(name)}
    </span>
  );
}
